import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Link } from 'react-router-dom';
import { FaExternalLinkAlt } from 'react-icons/fa';
import SEO from '../components/SEO';

const Projects: React.FC = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // ONLY 15 PROJECTS - WITH CORRECT IMAGE PATHS
  const projects = [
    {
      title: 'Johnson Graphic Solutions',
      description: 'Professional graphic design agency offering logo design, branding, web development, and social media marketing services.',
      image: '/images/projects/johnson-graphic.jpg',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://johnson-graphic-solutions.netlify.app/',
    },
    {
      title: '1 Sun Screens Services',
      description: 'Premium sun screen solutions for residential and commercial properties. Custom-fit screens, professional installation, and repair services.',
      image: '/images/projects/sun-screens.jpg',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'http://1-sun-screens-services.netlify.app/',
    },
    {
      title: 'Romero\'s Grass Installer',
      description: 'Professional grass installation and landscaping services. Expert turf installation, yard maintenance, and outdoor design solutions.',
      image: '/images/projects/romeros-grass.jpg',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://romeros-grass-installer.netlify.app/',
    },
    {
      title: 'Injouables',
      description: 'Creative digital solutions and innovative design services. Specializing in modern web experiences and brand identity.',
      image: '/images/projects/injouables.jpg',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://injouables.netlify.app/',
    },
    {
      title: 'Protex Facility Services LLC',
      description: 'Professional facility management and cleaning services. Comprehensive solutions for commercial and residential properties.',
      image: '/images/projects/protex-facility.jpg',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://protex-facility-services-llc.netlify.app/',
    },
    {
      title: 'Elite Crest',
      description: 'Premium design and branding services. Creating distinctive visual identities and marketing solutions for businesses.',
      image: '/images/projects/elite-crest.jpg',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://elite-crest.netlify.app/',
    },
    {
      title: 'Injouables 1',
      description: 'Innovative digital design solutions focused on creating engaging online experiences and powerful brand stories.',
      image: '/images/projects/injouables1.jpg',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://injouables1.netlify.app/',
    },
    {
      title: 'No Limit Earthworks LLC',
      description: 'Professional earthworks and excavation services. Land clearing, grading, site preparation, and construction solutions.',
      image: '/images/projects/no-limit-earthworks.jpg',
      tech: ['HTML5', 'CSS3', 'JavaScript', 'Netlify'],
      demo: 'https://no-limit-earthworks-llc.netlify.app/',
    },
    {
      title: 'Smart Student Productivity System',
      description: 'Advanced productivity system for students. Task management, study planning, progress tracking, and academic tools.',
      image: '/images/projects/smart-student.jpg',
      tech: ['React.js', 'Node.js', 'MongoDB', 'Express.js', 'JWT'],
      demo: 'https://smart-student-productivity-system.netlify.app/',
    },
    {
      title: 'Memory Remember Matching Game',
      description: 'Interactive memory matching game designed to improve cognitive skills. Fun and engaging brain training experience.',
      image: '/images/projects/memory-game.jpg',
      tech: ['JavaScript', 'HTML5', 'CSS3'],
      demo: 'https://memory-remember-matching-game.netlify.app/',
    },
    {
      title: 'Nexus 009',
      description: 'Modern digital platform showcasing innovative solutions and creative technologies. Built for performance and user experience.',
      image: '/images/projects/nexus-009.jpg',
      tech: ['Next.js', 'TypeScript', 'Tailwind CSS', 'Framer Motion'],
      demo: 'https://nexus009.netlify.app/',
    },
    {
      title: 'Nexus Task Manager System',
      description: 'Comprehensive task management system with team collaboration, real-time updates, and project tracking features.',
      image: '/images/projects/nexus-task-manager.jpg',
      tech: ['React.js', 'Node.js', 'PostgreSQL', 'WebSocket', 'Redis'],
      demo: 'https://nexus-task-manager-system.netlify.app/',
    },
    {
      title: 'ATS Premium Resume Builder',
      description: 'ATS-friendly resume builder with premium templates. Create professional resumes that pass automated screening systems.',
      image: '/images/projects/ats-resume.jpg',
      tech: ['React.js', 'TypeScript', 'Node.js', 'MongoDB', 'Tailwind CSS'],
      demo: 'https://ats-premium-resume-builder.netlify.app/',
    },
    {
      title: 'AI Professional Email Writer',
      description: 'AI-powered email writing assistant. Generate professional, context-aware emails for business communication.',
      image: '/images/projects/ai-email-writer.jpg',
      tech: ['React.js', 'Node.js', 'OpenAI API', 'Express.js', 'Tailwind CSS'],
      demo: 'https://ai-professional-email-writer.netlify.app/',
    },
    {
      title: 'Fanta Bottle Animated Website',
      description: 'Interactive animated website showcasing Fanta bottle with 3D-like animations and immersive user experience.',
      image: '/images/projects/fanta-bottle.jpg',
      tech: ['Three.js', 'GSAP', 'JavaScript', 'HTML5', 'CSS3'],
      demo: 'https://fanta-bottle-animated-website.netlify.app/',
    },
  ];

  return (
    <>
      <SEO 
        title="Projects" 
        description="Explore my portfolio of client projects - Web Development, Design, AI Tools, and more" 
      />
      
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-100 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 pt-24">
        <section className="py-12">
          <div className="container-custom px-4">
            <motion.div
              ref={ref}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6 }}
              className="text-center mb-16"
            >
              <div className="inline-block px-4 py-1 bg-primary/10 rounded-full text-primary text-sm font-medium mb-4">
                My Client Work
              </div>
              <h1 className="text-4xl md:text-5xl font-display font-bold text-primary mb-4">
                My <span className="text-gray-800 dark:text-gray-100">Projects</span>
              </h1>
              <p className="text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
                A showcase of my client projects - from design to deployment
              </p>
              <div className="w-24 h-1 bg-gradient-to-r from-primary to-secondary mx-auto rounded-full mt-4" />
            </motion.div>

            {/* All Projects Grid */}
            <div id="projects" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {projects.map((project, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: index * 0.05 }}
                  className="group"
                >
                  <div className="bg-white dark:bg-gray-900 rounded-2xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 hover:scale-[1.02] h-full flex flex-col">
                    {/* Image Container - Fixed Height with Overflow Hidden */}
                    <div className="relative w-full h-64 overflow-hidden bg-gray-100 dark:bg-gray-800">
                      {/* IMAGE SCROLL - NO ZOOM, FULL HEIGHT */}
                      <div 
                        className="w-full transition-transform duration-[3000ms] ease-out group-hover:-translate-y-[70%]"
                      >
                        <img
                          src={project.image}
                          alt={project.title}
                          className="w-full h-auto"
                          style={{ display: 'block' }}
                        />
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-5 flex flex-col flex-1">
                      <h3 className="text-lg font-display font-bold text-gray-800 dark:text-gray-100 mb-1">
                        {project.title}
                      </h3>
                      <p className="text-gray-600 dark:text-gray-300 text-xs leading-relaxed mb-3 line-clamp-2 flex-1">
                        {project.description}
                      </p>

                      {/* Tech Stack */}
                      <div className="flex flex-wrap gap-1.5 mb-3">
                        {project.tech.map((tech, idx) => (
                          <span
                            key={idx}
                            className="text-[10px] px-2 py-1 bg-gray-100 dark:bg-gray-800 rounded-full text-gray-600 dark:text-gray-300"
                          >
                            {tech}
                          </span>
                        ))}
                      </div>

                      {/* ONLY Live Demo Button */}
                      <motion.a
                        href={project.demo}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-2 px-4 py-2 bg-gradient-to-r from-primary to-secondary text-white rounded-lg text-xs hover:shadow-lg transition-all w-full"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <FaExternalLinkAlt className="text-sm" />
                        Live Demo
                      </motion.a>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Section */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="mt-16 text-center"
            >
              <div className="bg-gradient-to-r from-primary to-gray-800 rounded-3xl p-10 text-white">
                <h3 className="text-2xl font-display font-bold mb-3">
                  Have a Project in Mind?
                </h3>
                <p className="text-white/80 max-w-2xl mx-auto mb-6">
                  Let's collaborate and bring your ideas to life with cutting-edge technology
                </p>
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Link
                    to="/contact"
                    className="inline-block px-8 py-3 bg-white dark:bg-gray-800 text-primary rounded-xl font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                  >
                    Let's Work Together
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Projects;